import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useMapEntityEditor } from '@/features/map/hooks/useMapEntityEditor'
import { Shape } from '@/features/map/types/shape'
import { INVALID_SHAPE_KEYS, InvalidShapeReason } from '@/features/map/types/validation'
import { mapBuildingToShape } from '@/features/map/utils/mapMappers'
import { parseShapePoints } from '@/utils/shape'
import { Edit2, Eye, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapStage } from '../../map/components/MapState'
import { useMapDrawing } from '../../map/hooks/useMapDrawing'
import { DrawType, MapSelection, Mode } from '../../map/types/mapStateProps'
import { Building } from '../types/building'

type Props = {
  name: string
  imageId: string
  buildings: Building[]
  selectedBuildingId: string | null
  cancelSignal?: number
  canManage?: boolean
  loadingBuilding?: boolean

  onSelectBuilding: (id: string) => void
  onCreateBuilding?: (points: number[][]) => void
  onEditBuilding?: (payload: { building: Building; points?: number[][] }) => void
  onDeleteBuilding?: (id: string) => void
}

export function BuildingMap({
  name,
  imageId,
  buildings,
  selectedBuildingId,
  cancelSignal,
  canManage = false,
  loadingBuilding = false,
  onSelectBuilding,
  onCreateBuilding,
  onEditBuilding,
  onDeleteBuilding,
}: Props) {
  const { t } = useTranslation()
  const [building, setBuilding] = useState<Building | null>(null)

  const [mode, setMode] = useState<Mode>('view')
  const [drawType, setDrawType] = useState<DrawType>('shape')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [invalidShape, setInvalidShape] = useState<InvalidShapeReason | null>(null)

  useEffect(() => {
    handleCancelShape()
  }, [cancelSignal])

  useEffect(() => {
    if (!buildings) return
    const mapped: Shape[] = buildings.map(b => mapBuildingToShape(b, '#22c55e')).filter(Boolean) as Shape[]

    setShapes(mapped)

    if (selectedBuildingId) {
      handleSelectId(selectedBuildingId)
    } else {
      handleSelectId(null)
    }
  }, [buildings])

  const width = 600
  const height = 400

  const { points, handleClick, undo, resetPoints, finishShapeDrawing } = useMapDrawing(width, height, mode, drawType)
  const { shapes, setShapes, hasShapeCollision, handleSaveShape, handleEditShape, handleCancelShape } =
    useMapEntityEditor({})

  const notLocatedBuildings = buildings.filter(b => !b.shape)
  const invalidMessage =
    invalidShape === INVALID_SHAPE_KEYS.MINIMUM_POINTS
      ? t('common.map.minimumPoints')
      : invalidShape === INVALID_SHAPE_KEYS.COLLISION
        ? t('common.map.collision')
        : '\u00A0'

  const handleSelectId = (id: string | null) => {
    setSelectedId(id)
    setBuilding(buildings.find(f => f.id === id) ?? null)
    onSelectBuilding(id ?? '')
  }

  const handleSelect = (selection: MapSelection | null) => {
    if (!selection) {
      setSelectedId(null)
      setBuilding(null)
      onSelectBuilding('')
      return
    }

    if (selection.entity === 'shape' && selection.entityType === 'building') {
      const building = buildings.find(b => b.id === selection.id) ?? null

      setSelectedId(selection.id)
      setBuilding(building)
      onSelectBuilding(selection.id)
    }
  }

  const handleHover = (selection: MapSelection | null) => {
    if (!selection) {
      setHoveredId(null)
      return
    }

    if (selection.entity === 'shape' && selection.entityType === 'building') {
      setHoveredId(selection.id)
    }
  }

  const saveShape = () => {
    if (points.length < 6) {
      setInvalidShape(INVALID_SHAPE_KEYS.MINIMUM_POINTS)
      return
    }

    const result = finishShapeDrawing()
    if (!result) return

    if (hasShapeCollision(result, building?.id)) {
      setInvalidShape(INVALID_SHAPE_KEYS.COLLISION)
      return
    }

    setInvalidShape(null)
    handleSaveShape(result, 'building', building?.name)
    resetPoints()
    setMode('view')

    if (building) {
      const payload = { building, points: result }
      onEditBuilding?.(payload)
    } else {
      onCreateBuilding?.(result)
    }
  }

  const editShape = (id: string) => {
    handleEditShape(id)
    resetPoints()
    setMode('draw')
  }

  const cancelShape = () => {
    setInvalidShape(null)
    handleCancelShape()
    resetPoints()
    setMode('view')
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="flex-1">
          <SectionCard
            title={
              building
                ? t('floorplan.building.titleWithBuilding', {
                    hospitalName: name,
                    buildingName: building.name,
                  })
                : t('floorplan.building.title', {
                    hospitalName: name,
                  })
            }
            titleTestId="building-map-title"
            right={
              <>
                {canManage && mode == 'view' && (
                  <>
                    <Button
                      data-testid="building-map-add-location"
                      size="sm"
                      className="h-8 text-xs"
                      disabled={loadingBuilding}
                      onClick={() => {
                        setBuilding(null)
                        setSelectedId(null)
                        setMode('draw')
                      }}
                    >
                      <Plus className="size-3" />
                      {t('common.addLocation')}
                    </Button>
                    {building != null && (
                      <>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              data-testid="building-map-edit"
                              size="sm"
                              className="h-8 text-xs"
                              disabled={loadingBuilding}
                            >
                              <Edit2 className="size-3" />
                              {t('common.edit')}
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align="start" className="w-40">
                            <DropdownMenuItem
                              data-testid="building-map-edit-location"
                              onClick={() => {
                                editShape(selectedBuildingId ?? '')
                              }}
                            >
                              {t('common.editLocation')}
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              data-testid="building-map-edit-detail"
                              onClick={() => {
                                const existingPoints = building.shape ? parseShapePoints(building.shape) : undefined

                                onEditBuilding?.({
                                  building,
                                  points: existingPoints,
                                })
                              }}
                            >
                              {t('common.editDetail')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        <Button
                          data-testid="building-map-delete"
                          size="sm"
                          variant={'default'}
                          className="h-8 text-xs bg-destructive hover:bg-destructive/70"
                          disabled={loadingBuilding}
                          onClick={() => {
                            onDeleteBuilding?.(building.id)
                          }}
                        >
                          <Trash2 className="size-3" />
                          {t('common.delete')}
                        </Button>
                      </>
                    )}
                  </>
                )}
                {canManage && mode == 'draw' && (
                  <div className="flex gap-2">
                    <Button
                      data-testid="building-map-undo"
                      size="sm"
                      variant="outline"
                      onClick={() => undo()}
                      disabled={points.length < 2}
                      className="text-xs"
                    >
                      {t('common.undo')}
                    </Button>
                    <Button
                      data-testid="building-map-cancel"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        cancelShape()
                      }}
                      className="text-xs"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button data-testid="building-map-save" size="sm" onClick={() => saveShape()} className="text-xs">
                      {t('common.save')}
                    </Button>
                  </div>
                )}
              </>
            }
          >
            <div className="flex flex-col">
              <span className={`text-xs text-destructive text-end ${invalidShape ? 'visible' : 'invisible'}`}>
                {invalidMessage}
              </span>
              <MapStage
                imageId={imageId || ''}
                width={width}
                height={height}
                points={points}
                shapes={shapes}
                mode={mode}
                drawType={drawType}
                selectedId={selectedId}
                hoveredId={hoveredId}
                onMapClick={handleClick}
                onSelect={handleSelect}
                onHover={handleHover}
              />
            </div>
          </SectionCard>
        </div>

        {/* Side Panel */}
        <div className="w-full lg:w-72">
          <div className="flex flex-col gap-4">
            <SectionCard title={t('floorplan.noLocateBuildingTitle')} noCardTitle>
              <div className="mt-3 flex flex-col gap-2">
                {notLocatedBuildings.map(building => {
                  return (
                    <div
                      className="flex items-center justify-between text-xs gap-2"
                      key={building.id}
                      data-testid="building-map-not-located-item"
                    >
                      <span className="">{building.name}</span>
                      <Button
                        data-testid="building-map-not-located-view"
                        size="sm"
                        onClick={() => {
                          handleSelectId(building.id)
                        }}
                        className="text-xs"
                        disabled={building.id === selectedId}
                      >
                        <Eye className="size-3" />
                      </Button>
                    </div>
                  )
                })}
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  )
}
